import { useQueryClient } from '@tanstack/react-query';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { usePathname, useRouter, useSegments } from 'expo-router';
import React, { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';

import { useAuth } from '@/components/providers/AuthProvider';
import { usePermissionsTick } from '@/components/providers/PermissionsBootstrap';
import { useSignalR } from '@/components/providers/SignalRProvider';
import { communities, type CommunityCode } from '@/constants/communities';
import { useAppState } from '@/hooks/useAppState';
import { AccountService } from '@/services/domain/AccountService';
import { PushTokenPayload } from '@/services/domain/AccountTypes';

interface PushProviderProps {
  children: React.ReactNode;
}

interface NotificationData {
  messageId?: number | string | null;
  communityCode?: string | null;
}

interface IPushTokenInfo {
  token: string;
  platform: 'ios' | 'android';
}

/** Message thread id from URL (`/messages/123` or shared `/message/123`), or null if not on a thread. */
function getThreadIdFromPathname(pathname: string | null | undefined): number | null {
  if (typeof pathname !== 'string') return null;
  if (!pathname.startsWith('/messages/') && !pathname.startsWith('/message/')) return null;
  const parts = pathname.split('/');
  const idPart = parts[2];
  const n = parseInt(idPart ?? '', 10);
  return Number.isFinite(n) ? n : null;
}

function getNotificationData(notification: Notifications.Notification): NotificationData {
  if (Platform.OS === 'ios') {
    const trigger = notification.request.trigger as any;
    if (trigger?.type === 'push') {
      return (trigger.payload ?? {}) as NotificationData;
    }
  }

  return (notification.request.content.data ?? {}) as NotificationData;
}

function getNotificationMessageId(messageId: NotificationData['messageId']): number | null {
  if (typeof messageId !== 'string' && typeof messageId !== 'number') {
    return null;
  }

  const parsedMessageId = parseInt(String(messageId), 10);
  return Number.isFinite(parsedMessageId) ? parsedMessageId : null;
}

function isValidCommunityCode(
  communityCode: NotificationData['communityCode']
): communityCode is CommunityCode {
  return (
    typeof communityCode === 'string' &&
    Object.prototype.hasOwnProperty.call(communities, communityCode)
  );
}

function getPushType(platform: 'ios' | 'android'): PushTokenPayload['PushType'] {
  return platform === 'ios' ? 'APN' : 'FCM';
}

export const PushProvider: React.FC<PushProviderProps> = ({ children }) => {
  const { isAuthenticated, currentCommunity, switchCommunity, isLoading } = useAuth();
  const permissionsTick = usePermissionsTick();
  const { setMessageId } = useSignalR();
  const queryClient = useQueryClient();
  const currentPushTokenRef = useRef<IPushTokenInfo | null>(null);
  // Tracks the (token, community) tuple last POSTed (or currently being POSTed)
  // so concurrent syncTokenIfPossible calls don't re-send the same token. The
  // common race on Android: PermissionsBootstrap bumps `permissionsTick` AND
  // the system dialog dismissal bounces AppState through background→active,
  // firing onForeground; without this guard both paths POST in parallel.
  // Reset on logout so a re-login posts again.
  const sentPushTokenRef = useRef<{ token: string; community: CommunityCode } | null>(null);
  const sendInFlightRef = useRef<boolean>(false);
  const router = useRouter();
  const segments = useSegments();
  const pathname = usePathname();
  // Updated every render so async notification handlers see the current URL immediately.
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const segmentsRef = useRef<string[] | null>(null);
  segmentsRef.current = segments;
  const currentCommunityRef = useRef<CommunityCode | null>(currentCommunity ?? null);
  currentCommunityRef.current = currentCommunity ?? null;
  const isAuthenticatedRef = useRef<boolean>(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;
  // Always call the latest switchCommunity from inside async handlers so we don't
  // capture a stale closure (with empty `communityCredentials`) at mount time.
  // See Sentry GROKIO-APP-70: a stale switchCommunity used to wipe ApiService
  // configuration and persisted credentials when the app was opened from a push.
  const switchCommunityRef = useRef(switchCommunity);
  switchCommunityRef.current = switchCommunity;
  // Ensures the cold-start notification is only consumed once per app launch.
  const coldStartHandledRef = useRef<boolean>(false);
  // Cold-start + the response listener can both fire for the same tap, which would
  // run handleNotificationResponse twice and push the conversation route twice.
  const lastHandledNotificationResponseKeyRef = useRef<{ key: string; at: number } | null>(null);

  // Throws on failure so callers can roll back the sentPushTokenRef guard and
  // allow a retry. The single caller (syncTokenIfPossible) handles the catch.
  const sendPushTokenToBackend = useCallback(async (token: string, platform: 'ios' | 'android') => {
    const payload: PushTokenPayload = {
      Token: token,
      PushType: getPushType(platform),
    };
    await AccountService.setPushToken(payload);
    console.log('[PushProvider] Push token sent to backend:', payload.PushType);
  }, []);

  // Observation-only: the actual permission request is driven by
  // PermissionsBootstrap to avoid racing with the location prompt on Android.
  const getCurrentPushToken = useCallback(async (): Promise<IPushTokenInfo | null> => {
    if (!currentPushTokenRef.current) {
      const { status } = await Notifications.getPermissionsAsync();
      if (status !== 'granted') {
        console.log('[PushProvider] Push notification permissions not granted');
        return null;
      }

      const token = await Notifications.getDevicePushTokenAsync();
      currentPushTokenRef.current = {
        token: token.data as string,
        platform: token.type as 'ios' | 'android',
      };
    }

    return currentPushTokenRef.current;
  }, []);

  const triggerConversationRefresh = useCallback(
    (messageId: number) => {
      // Clear first so repeated notifications for the same conversation still
      // retrigger the ConversationScreen SignalR-style refresh effect.
      setMessageId(null);
      setTimeout(() => setMessageId(messageId), 0);
    },
    [setMessageId]
  );

  const handleNotificationResponse = useCallback(
    async (response: Notifications.NotificationResponse) => {
      const responseDedupeKey =
        response.notification.request.identifier || `date-${response.notification.date}`;
      const now = Date.now();
      const prev = lastHandledNotificationResponseKeyRef.current;
      if (prev && prev.key === responseDedupeKey && now - prev.at < 5000) {
        return;
      }
      lastHandledNotificationResponseKeyRef.current = { key: responseDedupeKey, at: now };

      const data = getNotificationData(response.notification);

      console.log('[PushProvider] Notification response data:', data);

      const { messageId, communityCode } = data;
      let didSwitchCommunity = false;

      // Switch community if needed (for any type of notification)
      if (communityCode) {
        if (!isValidCommunityCode(communityCode)) {
          console.warn('[PushProvider] Invalid community code:', communityCode);
          return;
        }

        if (communityCode !== currentCommunityRef.current) {
          didSwitchCommunity = true;
          console.log('[PushProvider] Switching community:', communityCode);
          try {
            if (router.canDismiss()) {
              router.dismissAll();
            }
            // Always call the latest switchCommunity (via ref) and await it before
            // navigating, so ApiService is configured for the target community.
            await switchCommunityRef.current(communityCode);
          } catch (error) {
            console.error('[PushProvider] Error switching community:', error);
            return;
          }
        }
      }

      // Handle message navigation
      const notificationMessageId = getNotificationMessageId(messageId);
      if (notificationMessageId !== null) {
        try {
          // Match current URL (refreshed every render) so we don't push a duplicate
          // screen if we're already on this thread; effect-based refs can lag one frame.
          const activeThreadId = didSwitchCommunity
            ? null
            : getThreadIdFromPathname(pathnameRef.current);
          if (activeThreadId === notificationMessageId) {
            // Already on this thread: nudge the same path as a hub message (see
            // SignalRProvider handleSignalRUpdate → ConversationScreen fetchNextPage).
            triggerConversationRefresh(notificationMessageId);
            return;
          }
          // Force the next ConversationScreen mount to fetch fresh data instead of
          // reusing a warm cache entry for this thread.
          queryClient.removeQueries({
            queryKey: ['conversation', notificationMessageId],
            exact: true,
          });
          // Build the messages stack explicitly as inbox -> conversation.
          // This avoids carrying cold-start redirect history underneath the
          // messages stack, which can leave a back button visible on root screens.
          // `deferLoad` tells MessagesScreen to skip its initial fetch while it is
          // only mounted as a back-target for the conversation; it will load on its
          // own when the user dismisses the conversation and the screen re-focuses.
          router.replace({
            pathname: '/messages/inbox',
            params: { deferLoad: '1' },
          });
          requestAnimationFrame(() => {
            if (router.canDismiss()) {
              router.dismissAll();
            }

            requestAnimationFrame(() => {
              router.push({
                pathname: '/messages/[id]',
                params: { id: String(notificationMessageId) },
              });
            });
          });
        } catch (error) {
          console.error('[PushProvider] Error handling notification navigation:', error);
        }
        return;
      }

      // If no message ID is found, go to notifications root.
      const isInNotificationsStack =
        !didSwitchCommunity && segmentsRef.current?.includes('(notifications)');
      if (isInNotificationsStack && router.canDismiss()) {
        router.dismissAll();
      } else if (!isInNotificationsStack) {        
        router.replace('/notifications');
        requestAnimationFrame(() => {
          if (router.canDismiss()) {
            router.dismissAll();
          }
        });
      } else {
        router.replace('/notifications');
      }
    },
    [queryClient, router, triggerConversationRefresh]
  );

  useEffect(() => {
    if (Platform.OS === 'web' || !Device.isDevice) {
      console.log('[PushProvider] Push notifications require a physical device');
      return;
    }

    Notifications.setNotificationHandler({
      handleNotification: async (notification) => {
        const data = getNotificationData(notification);
        const incomingMessageId = getNotificationMessageId(data.messageId);
        const activeMessageId = getThreadIdFromPathname(pathnameRef.current);
        const isActiveConversation =
          incomingMessageId !== null && activeMessageId === incomingMessageId;

        return {
          shouldPlaySound: false,
          shouldSetBadge: false,
          shouldShowBanner: !isActiveConversation,
          shouldShowList: true,
        } as any;
      },
    });
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web' || !Device.isDevice) {
      return;
    }

    const responseSubscription = Notifications.addNotificationResponseReceivedListener(
      handleNotificationResponse
    );

    return () => {
      responseSubscription.remove();
    };
  }, [handleNotificationResponse]);

  // A message push received while the app is foregrounded doubles as a refresh
  // signal: if SignalR is disconnected (or its hub event was missed), the
  // APN/FCM delivery still nudges the conversation and inbox queries through
  // the same path as a hub pushupdate. Cross-community pushes are ignored —
  // their ids belong to another community's data; switching only happens on tap.
  useEffect(() => {
    if (Platform.OS === 'web' || !Device.isDevice) {
      return;
    }

    const receivedSubscription = Notifications.addNotificationReceivedListener((notification) => {
      const data = getNotificationData(notification);
      const incomingMessageId = getNotificationMessageId(data.messageId);
      if (incomingMessageId === null) return;
      if (data.communityCode && data.communityCode !== currentCommunityRef.current) return;

      triggerConversationRefresh(incomingMessageId);
    });

    return () => {
      receivedSubscription.remove();
    };
  }, [triggerConversationRefresh]);

  useEffect(() => {
    if (Platform.OS === 'web' || !Device.isDevice) {
      return;
    }

    const initializePushNotifications = async () => {
      try {
        if (Platform.OS === 'android') {
          await Notifications.setNotificationChannelAsync('default', {
            name: 'Default',
            importance: Notifications.AndroidImportance.DEFAULT,
          });
        }
      } catch (error) {
        console.error('[PushProvider] Error initializing push notifications:', error);
      }
    };

    initializePushNotifications();
  }, []);

  // Cold-start: handle the notification that launched the app, but only after
  // AuthProvider has finished loading credentials/current community. Otherwise
  // currentCommunityRef/switchCommunityRef would be stale and cause the
  // "no community selected" state corruption (Sentry GROKIO-APP-70).
  useEffect(() => {
    if (Platform.OS === 'web' || !Device.isDevice) return;
    if (isLoading) return;
    if (coldStartHandledRef.current) return;
    coldStartHandledRef.current = true;

    const lastNotificationResponse = Notifications.getLastNotificationResponse();
    if (lastNotificationResponse) {
      // Clear immediately so subsequent launches from the launcher don't re-trigger
      // navigation (Android persists this value otherwise).
      Notifications.clearLastNotificationResponse();
      handleNotificationResponse(lastNotificationResponse);
    }
  }, [isLoading, handleNotificationResponse]);

  const syncTokenIfPossible = useCallback(async () => {
    const community = currentCommunityRef.current;
    if (Platform.OS === 'web' || !isAuthenticatedRef.current || !community || !Device.isDevice) {
      return;
    }
    if (sendInFlightRef.current) return;
    try {
      const pushToken = await getCurrentPushToken();
      if (!pushToken) return;
      const sent = sentPushTokenRef.current;
      if (sent && sent.token === pushToken.token && sent.community === community) return;
      // Optimistically claim the slot BEFORE awaiting the POST so any
      // concurrent caller (e.g. AppState onForeground bouncing while this
      // POST is still in flight) sees the guard and bails. If the POST
      // throws, the catch below clears the slot so a future call can retry.
      sentPushTokenRef.current = { token: pushToken.token, community };
      sendInFlightRef.current = true;
      try {
        await sendPushTokenToBackend(pushToken.token, pushToken.platform);
      } catch (error) {
        sentPushTokenRef.current = null;
        throw error;
      } finally {
        sendInFlightRef.current = false;
      }
    } catch (error) {
      console.error('[PushProvider] Error syncing push token:', error);
    }
  }, [getCurrentPushToken, sendPushTokenToBackend]);

  // Clear the "last sent" guard on sign-out so the next sign-in re-POSTs.
  useEffect(() => {
    if (!isAuthenticated) {
      sentPushTokenRef.current = null;
    }
  }, [isAuthenticated]);

  // Sync on auth change OR whenever PermissionsBootstrap signals a permission
  // change (`permissionsTick`). The tick is the iOS-friendly trigger: native
  // permission dialogs there don't take the app through background→active, so
  // AppState-based retries don't fire reliably. On Android the AppState
  // listener below still acts as a redundant safety net.
  useEffect(() => {
    if (!isAuthenticated || !currentCommunity) return;
    syncTokenIfPossible();
  }, [currentCommunity, isAuthenticated, permissionsTick, syncTokenIfPossible]);

  // Retry on foreground: on Android, dismissing the system permission dialog
  // bounces AppState back to 'active' through 'background'. iOS only goes
  // through 'inactive' (which `useAppState` filters out), so this is mostly
  // an Android safety net to register the token if the tick path missed.
  useAppState({
    onForeground: () => {
      if (currentPushTokenRef.current) return;
      syncTokenIfPossible();
    },
  });

  return <>{children}</>;
};
